/**
 * ApiAppsManager — admin UI for the EURION Open API programme.
 *
 * Sections:
 *  - OAuth2 Applications list + create/delete/rotate-secret
 *  - API Keys per app
 *  - Webhook subscriptions per app
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authHeaders, errorMessage, showAdminError } from './adminApi';

interface ApiApp {
  id:                  string;
  name:                string;
  client_id:           string;
  client_secret_prefix: string;
  status:              'ACTIVE' | 'SUSPENDED' | 'REVOKED';
  grant_types:         string[];
  allowed_scopes:      string[];
  rate_limit_rpm:      number;
  created_at:          string;
}

interface Webhook {
  id:        string;
  url:       string;
  events:    string[];
  is_active: boolean;
}

async function apiFetch<T>(url: string, opts?: RequestInit): Promise<T> {
  // session token + Content-Type only with a body (see adminApi.ts); failed actions are shown
  const headers: Record<string, string> = { ...authHeaders(), ...(opts?.body != null ? { 'Content-Type': 'application/json' } : {}), ...(opts?.headers as Record<string, string> | undefined) };
  const res = await fetch(url, { ...opts, headers });
  if (!res.ok) {
    const msg = await errorMessage(res);
    if ((opts?.method ?? 'GET') !== 'GET') showAdminError(msg);
    throw new Error(msg);
  }
  return (await res.json() as { data: T }).data;
}

const AVAILABLE_SCOPES = [
  'messages:read', 'messages:write',
  'files:read', 'files:write', 'users:read',
  'calls:read', 'org:read', 'audit:read',
  'webhooks:manage', 'admin:read',
];

export function ApiAppsManager() {
  const { t } = useTranslation();
  const qc    = useQueryClient();

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName]       = useState('');
  const [newDesc, setNewDesc]       = useState('');
  const [newScopes, setNewScopes]   = useState<string[]>(['messages:read']);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newSecret, setNewSecret]   = useState<string | null>(null);
  const [selectedApp, setSelectedApp] = useState<ApiApp | null>(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState('');

  const appsQ = useQuery({
    queryKey: ['api-apps'],
    queryFn:  () => apiFetch<{ apps: ApiApp[] }>('/v1/apps'),
  });

  const webhooksQ = useQuery({
    queryKey: ['webhooks', selectedApp?.id],
    queryFn:  () => apiFetch<{ webhooks: Webhook[] }>(`/v1/apps/${selectedApp!.id}/webhooks`),
    enabled:  !!selectedApp,
  });

  const createMutation = useMutation({
    mutationFn: (body: unknown) => apiFetch<{ app: ApiApp; clientSecret: string }>('/v1/apps', {
      method: 'POST', body: JSON.stringify(body),
    }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['api-apps'] });
      setNewSecret(data.clientSecret);
      setShowCreate(false);
      setNewName('');
    },
    onError: (err: Error) => setCreateError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiFetch(`/v1/apps/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['api-apps'] });
      if (selectedApp) setSelectedApp(null);
    },
  });

  const rotateMutation = useMutation({
    mutationFn: (id: string) => apiFetch<{ clientSecret: string }>(`/v1/apps/${id}/rotate-secret`, { method: 'POST' }),
    onSuccess: (data) => setNewSecret(data.clientSecret),
  });

  const addWebhookMutation = useMutation({
    mutationFn: (body: unknown) => apiFetch(`/v1/apps/${selectedApp!.id}/webhooks`, {
      method: 'POST', body: JSON.stringify(body),
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['webhooks', selectedApp?.id] });
      setWebhookUrl('');
      setWebhookEvents('');
    },
  });

  const toggleScope = (scope: string) =>
    setNewScopes(s => s.includes(scope) ? s.filter(x => x !== scope) : [...s, scope]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-nord-4">{t('adminUi.apiApps.title', 'API Applications')}</h2>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="rounded-lg bg-nord-frost-1 px-4 py-2 text-sm font-semibold text-nord-0 hover:bg-nord-frost-2"
        >
          {t('adminUi.apiApps.create', '+ New App')}
        </button>
      </div>

      {/* New secret banner */}
      {newSecret && (
        <div className="rounded-lg border border-nord-14/60 bg-nord-14/20 p-4">
          <p className="text-sm font-semibold text-nord-14">{t('adminUi.apiApps.secretOnce', 'Save this secret — it will not be shown again!')}</p>
          <code className="mt-2 block break-all rounded bg-nord-0 px-3 py-2 text-sm text-nord-4 font-mono">
            {newSecret}
          </code>
          <button
            onClick={() => setNewSecret(null)}
            className="mt-2 text-xs text-nord-14 underline"
          >
            {t('adminUi.dismiss', 'Dismiss')}
          </button>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <div className="rounded-lg border border-nord-2 bg-nord-1 p-5 flex flex-col gap-3">
          <h3 className="text-sm font-semibold text-nord-4">{t('adminUi.apiApps.newApp', 'Register Application')}</h3>
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder={t('adminUi.apiApps.appName', 'Application name')}
            className="rounded-lg border border-nord-3 bg-nord-0 px-3 py-2 text-sm text-nord-4 placeholder-nord-muted focus:border-nord-frost-1 focus:outline-none"
          />
          <input
            type="text"
            value={newDesc}
            onChange={e => setNewDesc(e.target.value)}
            placeholder={t('adminUi.apiApps.appDesc', 'Description (optional)')}
            className="rounded-lg border border-nord-3 bg-nord-0 px-3 py-2 text-sm text-nord-4 placeholder-nord-muted focus:border-nord-frost-1 focus:outline-none"
          />
          <div>
            <p className="mb-2 text-xs font-medium text-nord-muted">{t('adminUi.apiApps.scopes', 'Scopes')}</p>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_SCOPES.map(scope => (
                <label key={scope} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newScopes.includes(scope)}
                    onChange={() => toggleScope(scope)}
                    className="rounded"
                  />
                  <span className="text-xs text-nord-muted hover:text-nord-4 font-mono">{scope}</span>
                </label>
              ))}
            </div>
          </div>
          {createError && <p className="text-sm text-nord-11">{createError}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowCreate(false)} className="text-sm text-nord-muted hover:text-nord-4">
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={() => createMutation.mutate({ name: newName, description: newDesc, allowedScopes: newScopes })}
              disabled={createMutation.isPending || !newName}
              className="rounded-lg bg-nord-frost-1 px-4 py-2 text-sm font-semibold text-nord-0 hover:bg-nord-frost-2 disabled:opacity-50"
            >
              {createMutation.isPending ? t('adminUi.creating', 'Creating…') : t('adminUi.apiApps.register', 'Register')}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Apps list */}
        <div className="flex flex-col gap-2">
          {appsQ.isLoading && <p className="text-sm text-nord-muted">{t('common.loading', 'Loading…')}</p>}
          {appsQ.data?.apps.map(app => (
            <div
              key={app.id}
              onClick={() => setSelectedApp(app)}
              className={[
                'cursor-pointer rounded-lg border p-4 transition-colors',
                selectedApp?.id === app.id
                  ? 'border-nord-frost-1 bg-nord-frost-1/10'
                  : 'border-nord-2 bg-nord-1 hover:border-nord-frost-1/40',
              ].join(' ')}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-nord-4">{app.name}</p>
                  <p className="mt-0.5 text-xs font-mono text-nord-muted">
                    client_id: {app.client_id}
                  </p>
                  <p className="text-xs font-mono text-nord-muted">
                    secret: {app.client_secret_prefix}…
                  </p>
                </div>
                <span className={[
                  'rounded px-2 py-0.5 text-xs font-semibold',
                  app.status === 'ACTIVE' ? 'bg-nord-14/20 text-nord-14' : 'bg-nord-11/20 text-nord-11',
                ].join(' ')}>
                  {app.status}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {app.allowed_scopes.map(s => (
                  <span key={s} className="rounded bg-nord-2 px-1.5 py-0.5 text-[10px] font-mono text-nord-muted">{s}</span>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  onClick={e => { e.stopPropagation(); rotateMutation.mutate(app.id); }}
                  className="text-xs text-nord-frost-1 hover:underline"
                >
                  {t('adminUi.apiApps.rotateSecret', 'Rotate secret')}
                </button>
                <button
                  onClick={e => { e.stopPropagation(); deleteMutation.mutate(app.id); }}
                  className="text-xs text-nord-11 hover:underline"
                >
                  {t('adminUi.apiApps.revoke', 'Revoke')}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Webhooks panel */}
        {selectedApp && (
          <div className="rounded-lg border border-nord-2 bg-nord-1 p-5 flex flex-col gap-3">
            <h3 className="text-sm font-semibold text-nord-4">
              {t('adminUi.apiApps.webhooks', 'Webhooks')} — {selectedApp.name}
            </h3>
            <div className="flex gap-2">
              <input
                type="url"
                value={webhookUrl}
                onChange={e => setWebhookUrl(e.target.value)}
                placeholder="https://your-app.eu/webhook"
                className="flex-1 rounded-lg border border-nord-3 bg-nord-0 px-3 py-2 text-xs text-nord-4 placeholder-nord-muted focus:border-nord-frost-1 focus:outline-none"
              />
              <input
                type="text"
                value={webhookEvents}
                onChange={e => setWebhookEvents(e.target.value)}
                placeholder="message.created,room.updated"
                className="flex-1 rounded-lg border border-nord-3 bg-nord-0 px-3 py-2 text-xs text-nord-4 placeholder-nord-muted focus:border-nord-frost-1 focus:outline-none"
              />
              <button
                onClick={() => addWebhookMutation.mutate({
                  url: webhookUrl,
                  events: webhookEvents.split(',').map(s => s.trim()).filter(Boolean),
                })}
                disabled={addWebhookMutation.isPending || !webhookUrl}
                aria-label={t('adminUi.apiApps.addWebhook', 'Add webhook')}
                title={t('adminUi.apiApps.addWebhook', 'Add webhook')}
                className="rounded-lg bg-nord-frost-1 px-3 py-1.5 text-xs font-semibold text-nord-0 hover:bg-nord-frost-2 disabled:opacity-50"
              >
                +
              </button>
            </div>
            {webhooksQ.data?.webhooks.map(wh => (
              <div key={wh.id} className="flex items-center justify-between rounded bg-nord-0 p-3">
                <div>
                  <p className="text-xs font-mono text-nord-4 truncate">{wh.url}</p>
                  <p className="text-xs text-nord-muted">{wh.events.join(', ')}</p>
                </div>
                <span className={`rounded px-2 py-0.5 text-xs ${wh.is_active ? 'text-nord-14' : 'text-nord-muted'}`}>
                  {wh.is_active ? t('adminUi.active', 'Active') : t('adminUi.inactive', 'Inactive')}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
